import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { AuditTrailEntry } from "@thalamos/haven-common";

/**
 * One entry in full, as it was stored.
 *
 * The table shows the fields worth scanning; this is for the rest — the payload,
 * the trace id, the session digest, the code version. Rendered as raw JSON
 * rather than a field-by-field layout because an entry's payload shape depends
 * on its kind, and a layout per kind would be a component nobody updates when a
 * kind is added.
 *
 * Deliberately no JSON-viewer dependency. eMHA's equivalent uses one; a `<pre>`
 * shows the same thing, and the entry is small enough that collapsing does not
 * earn a package.
 *
 * Nothing is redacted here, which is safe only because of who can reach this
 * area at all — the endpoint returns payloads as stored and says so.
 */
export const AuditEntryDialog = ({
  entry,
  onClose,
}: {
  entry: AuditTrailEntry | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={entry !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
      >
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          {entry?.kind}
        </Typography>
        <IconButton onClick={onClose} aria-label={t("admin.trail.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          component="pre"
          variant="body2"
          sx={{
            m: 0,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {entry === null ? "" : JSON.stringify(entry, null, 2)}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};
